import { useEffect, useState } from "react";
import { getNextToolsState } from "@/lib/utils/task-form";

export function useTaskToolSelection(allIntegrationIds: number[]) {
	const [selectedTools, setSelectedTools] = useState<string[]>(["auto"]);
	const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<number[]>([]);

	const handleToggleTool = (toolId: string) => {
		const nextTools = getNextToolsState(selectedTools, toolId);
		setSelectedTools(nextTools);

		if (nextTools.includes("auto")) {
			setSelectedIntegrationIds((prev) => {
				if (allIntegrationIds.length === 0) {
					return prev.length === 0 ? prev : [];
				}

				const shouldUpdate =
					prev.length !== allIntegrationIds.length ||
					prev.some((id) => !allIntegrationIds.includes(id));

				return shouldUpdate ? [...allIntegrationIds] : prev;
			});
			return;
		}

		const appIntegrationId = Number(toolId);
		if (!Number.isFinite(appIntegrationId)) return;

		setSelectedIntegrationIds((prev) =>
			prev.includes(appIntegrationId)
				? prev.filter((id) => id !== appIntegrationId)
				: [...prev, appIntegrationId]
		);
	};

	useEffect(() => {
		if (!selectedTools.includes("auto")) {
			return;
		}

		setSelectedIntegrationIds((prev) => {
			if (allIntegrationIds.length === 0) {
				return prev.length === 0 ? prev : [];
			}

			const shouldUpdate =
				prev.length !== allIntegrationIds.length ||
				prev.some((id) => !allIntegrationIds.includes(id));

			return shouldUpdate ? [...allIntegrationIds] : prev;
		});
	}, [allIntegrationIds, selectedTools]);

	useEffect(() => {
		if (selectedTools.length === 0 || selectedTools.includes("auto")) {
			return;
		}

		const appIntegrationIds = selectedTools
			.map((toolId) => Number(toolId))
			.filter((id): id is number => Number.isFinite(id));

		if (appIntegrationIds.length === 0) return;

		setSelectedIntegrationIds((prev) => {
			if (prev.length === appIntegrationIds.length) {
				const prevSorted = [...prev].sort();
				const resolvedSorted = [...appIntegrationIds].sort();
				if (prevSorted.every((value, index) => value === resolvedSorted[index])) return prev;
			}
			return appIntegrationIds;
		});
	}, [selectedTools]);

	return {
		selectedTools,
		selectedIntegrationIds,
		setSelectedTools,
		setSelectedIntegrationIds,
		handleToggleTool,
	};
}
